import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Snackbar, Stack,
  Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { apiHost } from '../../utils/apiHost';

// Supplier, pesanan pembelian (PO), penerimaan barang bertahap, pembayaran & utang supplier.
// Server: controller/pembelianController.js. Penerimaan menambah stok bahan baku / produk; produk beresep tidak bisa diterima.

const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);
const cfg = { withCredentials: true };
const rupiah = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const angka = (v) => (Number.isFinite(Number(v)) ? Number(v).toLocaleString('id-ID', { maximumFractionDigits: 3 }) : '-');
const tgl = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');
const hariIni = () => { const d = new Date(); const p = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const idBaru = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
const pesanGalat = (e) => e?.response?.data?.message || e?.message || 'Terjadi kesalahan';

const STATUS = {
  draft: { label: 'Draf', color: 'default' },
  dipesan: { label: 'Dipesan', color: 'info' },
  sebagian: { label: 'Diterima sebagian', color: 'warning' },
  selesai: { label: 'Selesai', color: 'success' },
  dibatalkan: { label: 'Dibatalkan', color: 'error' },
};
const BAYAR = {
  belum_ada_tagihan: { label: 'Belum ada tagihan', color: 'default' },
  uang_muka: { label: 'Uang muka', color: 'info' },
  belum: { label: 'Belum dibayar', color: 'error' },
  sebagian: { label: 'Dibayar sebagian', color: 'warning' },
  lunas: { label: 'Lunas', color: 'success' },
  batal: { label: '-', color: 'default' },
};

const Dlg = ({ buka, judul, onTutup, onSimpan, proses, galat, children, simpanLabel = 'Simpan', maxWidth = 'xs', aksiTambahan }) => (
  <Dialog open={buka} onClose={proses ? undefined : onTutup} fullWidth maxWidth={maxWidth}>
    <DialogTitle>{judul}</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {galat && <Alert severity="error">{galat}</Alert>}
        {children}
      </Stack>
    </DialogContent>
    <DialogActions>
      {aksiTambahan}
      <Box sx={{ flex: 1 }} />
      <Button onClick={onTutup} disabled={proses}>Batal</Button>
      {onSimpan && <Button variant="contained" onClick={onSimpan} disabled={proses}>{proses ? 'Menyimpan…' : simpanLabel}</Button>}
    </DialogActions>
  </Dialog>
);

// ---------------- Form PO ----------------
const FormPo = ({ buka, awal, cabang, suppliers, bahan, produk, onTutup, onSelesai }) => {
  const [f, setF] = useState({});
  const [baris, setBaris] = useState([]);
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const qs = cabang ? `cabang=${cabang}` : '';

  React.useEffect(() => {
    if (!buka) return;
    setGalat('');
    if (awal) {
      setF({ supplieruuid: awal.supplieruuid, tanggalPesan: awal.tanggalPesan, perkiraanTiba: awal.perkiraanTiba || '', jatuhTempo: awal.jatuhTempo || '', catatan: awal.catatan || '' });
      setBaris((awal.items || []).map((i) => ({ jenis: i.jenis, id: i.bahanuuid || i.baranguuid, nama: i.nama, satuan: i.satuan, jumlah: String(i.jumlahPesan), harga: String(i.harga) })));
    } else {
      setF({ supplieruuid: '', tanggalPesan: hariIni(), perkiraanTiba: '', jatuhTempo: '', catatan: '' });
      setBaris([{ jenis: 'bahan', id: '', nama: '', satuan: '', jumlah: '', harga: '' }]);
    }
  }, [buka, awal]);

  const ubahBaris = (i, patch) => setBaris((b) => b.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const pilihItem = async (i, o) => {
    if (!o) { ubahBaris(i, { id: '', nama: '', satuan: '' }); return; }
    const jenis = baris[i].jenis;
    const id = jenis === 'bahan' ? o.uuid : o.baranguuid;
    const nama = jenis === 'bahan' ? o.nama : o.namabarang;
    ubahBaris(i, { id, nama, satuan: jenis === 'bahan' ? o.satuan : 'pcs', harga: baris[i].harga || String(jenis === 'bahan' ? o.hargaPerSatuan || '' : o.hargaModal || '') });
    try {
      const r = await ambil(`${base()}/pesanan-beli-harga-terakhir?jenis=${jenis}&id=${id}${f.supplieruuid ? `&supplier=${f.supplieruuid}` : ''}${qs ? `&${qs}` : ''}`);
      if (r.data?.harga) ubahBaris(i, { harga: String(r.data.harga), acuan: `harga terakhir ${r.data.nomor || ''}` });
    } catch (_) { /* acuan tidak wajib */ }
  };
  const total = baris.reduce((a, x) => a + (Number(x.jumlah) || 0) * (Number(x.harga) || 0), 0);

  const simpan = async (kirim) => {
    setProses(true); setGalat('');
    const body = {
      cabang: cabang || undefined, supplieruuid: f.supplieruuid, tanggalPesan: f.tanggalPesan, perkiraanTiba: f.perkiraanTiba || undefined, jatuhTempo: f.jatuhTempo || undefined, catatan: f.catatan,
      items: baris.filter((x) => x.id).map((x) => (x.jenis === 'bahan' ? { jenis: 'bahan', bahanuuid: x.id, jumlah: x.jumlah, harga: x.harga } : { jenis: 'produk', baranguuid: x.id, jumlah: x.jumlah, harga: x.harga })),
      kirim,
    };
    try {
      const r = awal
        ? await axios.put(`${base()}/pesanan-beli/${awal.uuid}`, body, cfg)
        : await axios.post(`${base()}/pesanan-beli`, body, cfg);
      if (awal && kirim) await axios.post(`${base()}/pesanan-beli/${awal.uuid}/pesan`, {}, cfg);
      onSelesai(r.data?.message || 'Pesanan disimpan');
    } catch (e) {
      setGalat(pesanGalat(e));
    }
    setProses(false);
  };

  return (
    <Dlg
      buka={buka} judul={awal ? `Ubah draf ${awal.nomor}` : 'Buat pesanan pembelian'} onTutup={onTutup} proses={proses} galat={galat} maxWidth="md"
      aksiTambahan={<Typography sx={{ fontWeight: 800 }}>Total {rupiah(total)}</Typography>}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField select fullWidth size="small" label="Supplier" value={f.supplieruuid || ''} onChange={(e) => setF({ ...f, supplieruuid: e.target.value })}>
          {suppliers.filter((s) => s.aktif).map((s) => <MenuItem key={s.uuid} value={s.uuid}>{s.nama}</MenuItem>)}
        </TextField>
        <TextField size="small" type="date" label="Tanggal pesan" InputLabelProps={{ shrink: true }} value={f.tanggalPesan || ''} onChange={(e) => setF({ ...f, tanggalPesan: e.target.value })} />
        <TextField size="small" type="date" label="Perkiraan tiba" InputLabelProps={{ shrink: true }} value={f.perkiraanTiba || ''} onChange={(e) => setF({ ...f, perkiraanTiba: e.target.value })} />
        <TextField size="small" type="date" label="Jatuh tempo bayar" InputLabelProps={{ shrink: true }} value={f.jatuhTempo || ''} onChange={(e) => setF({ ...f, jatuhTempo: e.target.value })} />
      </Stack>
      <TextField size="small" label="Catatan" value={f.catatan || ''} onChange={(e) => setF({ ...f, catatan: e.target.value })} />
      <Typography variant="subtitle2">Barang yang dipesan</Typography>
      {baris.map((x, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <ToggleButtonGroup exclusive size="small" value={x.jenis} onChange={(_, v) => v && ubahBaris(i, { jenis: v, id: '', nama: '', satuan: '', harga: '' })}>
            <ToggleButton value="bahan">Bahan</ToggleButton>
            <ToggleButton value="produk">Produk</ToggleButton>
          </ToggleButtonGroup>
          <Autocomplete
            sx={{ flex: 1, minWidth: 180 }} size="small"
            options={x.jenis === 'bahan' ? bahan : produk.filter((p) => !p.ikutBahan)}
            value={x.id ? (x.jenis === 'bahan' ? bahan.find((b) => b.uuid === x.id) : produk.find((p) => p.baranguuid === x.id)) || null : null}
            onChange={(_, v) => pilihItem(i, v)}
            getOptionLabel={(o) => (x.jenis === 'bahan' ? `${o.nama} (${o.satuan})` : o.namabarang)}
            isOptionEqualToValue={(a, b) => (a.uuid || a.baranguuid) === (b.uuid || b.baranguuid)}
            renderInput={(p) => <TextField {...p} label={x.jenis === 'bahan' ? 'Bahan' : 'Produk (tanpa resep)'} />}
          />
          <TextField size="small" type="number" label={`Jumlah${x.satuan ? ` (${x.satuan})` : ''}`} sx={{ width: 140 }} value={x.jumlah} onChange={(e) => ubahBaris(i, { jumlah: e.target.value })} />
          <TextField size="small" type="number" label="Harga satuan" sx={{ width: 140 }} value={x.harga} onChange={(e) => ubahBaris(i, { harga: e.target.value })} helperText={x.acuan || ' '} />
          <Typography variant="body2" sx={{ width: 100, textAlign: 'right' }}>{rupiah((Number(x.jumlah) || 0) * (Number(x.harga) || 0))}</Typography>
          <IconButton size="small" onClick={() => setBaris(baris.filter((_, j) => j !== i))} aria-label="Hapus baris">✕</IconButton>
        </Stack>
      ))}
      <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => setBaris([...baris, { jenis: 'bahan', id: '', nama: '', satuan: '', jumlah: '', harga: '' }])}>+ Tambah barang</Button>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={() => simpan(false)} disabled={proses}>Simpan draf</Button>
        <Button variant="contained" onClick={() => simpan(true)} disabled={proses}>Simpan &amp; pesan</Button>
      </Stack>
    </Dlg>
  );
};

// ---------------- Detail PO ----------------
const DetailPo = ({ uuid, cabang, onTutup, onUbah, kabari, muat }) => {
  const { data, mutate } = useSWR(uuid ? `${base()}/pesanan-beli/${uuid}` : null, ambil, { revalidateOnFocus: false });
  const po = data?.data;
  const [sub, setSub] = useState(null); // 'terima' | 'bayar'
  const [f, setF] = useState({});
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const [perluCatat, setPerluCatat] = useState(null);
  const [clientId, setClientId] = useState(idBaru);

  const segarkan = async () => { await mutate(); await muat(); };
  const aksi = async (fn, pesan) => {
    try { await fn(); kabari(pesan); await segarkan(); } catch (e) { kabari(pesanGalat(e), 'error'); }
  };
  const bukaTerima = () => {
    setGalat(''); setPerluCatat(null); setClientId(idBaru());
    setF({ tanggal: hariIni(), catatan: '', jumlah: Object.fromEntries(po.items.map((i) => [i.uuid, i.sisa > 0 ? String(i.sisa) : ''])) });
    setSub('terima');
  };
  const bukaBayar = () => { setGalat(''); setClientId(idBaru()); setF({ tanggal: hariIni(), jumlah: po.sisaTagihan > 0 ? String(po.sisaTagihan) : '', metode: 'tunai', catatan: '' }); setSub('bayar'); };

  const kirim = async () => {
    setProses(true); setGalat(''); setPerluCatat(null);
    try {
      if (sub === 'terima') {
        await axios.post(`${base()}/pesanan-beli/${uuid}/terima`, { clientId, tanggal: f.tanggal, catatan: f.catatan, items: po.items.map((i) => ({ itemuuid: i.uuid, jumlah: f.jumlah[i.uuid] || 0 })) }, cfg);
        kabari('Barang diterima, stok bertambah');
      } else {
        await axios.post(`${base()}/pesanan-beli/${uuid}/bayar`, { clientId, tanggal: f.tanggal, jumlah: f.jumlah, metode: f.metode, catatan: f.catatan }, cfg);
        kabari('Pembayaran dicatat');
      }
      setSub(null);
      await segarkan();
    } catch (e) {
      setGalat(pesanGalat(e));
      if (e?.response?.data?.kode === 'BAHAN_BELUM_DICATAT') setPerluCatat(e.response.data.bahanuuid);
    }
    setProses(false);
  };
  const catatBahan = async () => {
    try {
      await axios.post(`${base()}/bahan/${perluCatat}/catat`, cabang ? { cabang } : {}, cfg);
      setPerluCatat(null); setGalat('Bahan dicatat di cabang ini. Tekan "Terima" lagi.');
    } catch (e) { setGalat(pesanGalat(e)); }
  };

  if (!uuid) return null;
  return (
    <>
      <Dialog open onClose={onTutup} fullWidth maxWidth="md">
        <DialogTitle>
          {po ? po.nomor : 'Memuat…'}
          {po && <Chip size="small" sx={{ ml: 1 }} color={STATUS[po.status].color} label={STATUS[po.status].label} />}
          {po && <Chip size="small" sx={{ ml: 1 }} color={BAYAR[po.statusBayar].color} label={BAYAR[po.statusBayar].label} />}
        </DialogTitle>
        <DialogContent dividers>
          {!po ? <Typography>Memuat…</Typography> : (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Supplier <b>{po.supplier?.nama}</b>{po.supplier?.hp ? ` · ${po.supplier.hp}` : ''} · Dipesan {tgl(po.tanggalPesan)}
                {po.perkiraanTiba ? ` · Perkiraan tiba ${tgl(po.perkiraanTiba)}` : ''}{po.jatuhTempo ? ` · Jatuh tempo bayar ${tgl(po.jatuhTempo)}` : ''}{po.catatan ? ` · ${po.catatan}` : ''}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead><TableRow><TableCell>Barang</TableCell><TableCell align="right">Dipesan</TableCell><TableCell align="right">Diterima</TableCell><TableCell align="right">Sisa</TableCell><TableCell align="right">Harga</TableCell><TableCell align="right">Subtotal</TableCell></TableRow></TableHead>
                  <TableBody>
                    {po.items.map((i) => (
                      <TableRow key={i.uuid}>
                        <TableCell>{i.nama} <Typography component="span" variant="caption" color="text.secondary">({i.jenis}, {i.satuan})</Typography></TableCell>
                        <TableCell align="right">{angka(i.jumlahPesan)}</TableCell>
                        <TableCell align="right">{angka(i.jumlahTerima)}</TableCell>
                        <TableCell align="right" sx={{ color: i.sisa > 0 && po.status !== 'draft' ? 'warning.main' : undefined }}>{angka(i.sisa)}</TableCell>
                        <TableCell align="right">{rupiah(i.harga)}</TableCell>
                        <TableCell align="right">{rupiah(i.subtotal)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow><TableCell colSpan={5} align="right"><b>Total pesanan</b></TableCell><TableCell align="right"><b>{rupiah(po.totalPesan)}</b></TableCell></TableRow>
                    <TableRow><TableCell colSpan={5} align="right">Tagihan (barang diterima)</TableCell><TableCell align="right">{rupiah(po.totalDiterima)}</TableCell></TableRow>
                    <TableRow><TableCell colSpan={5} align="right">Sudah dibayar</TableCell><TableCell align="right">{rupiah(po.totalBayar)}</TableCell></TableRow>
                    <TableRow><TableCell colSpan={5} align="right"><b>Sisa utang</b></TableCell><TableCell align="right" sx={{ color: po.sisaTagihan > 0 ? 'error.main' : undefined }}><b>{rupiah(po.sisaTagihan)}</b></TableCell></TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {po.penerimaan.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Riwayat penerimaan</Typography>
                  {po.penerimaan.map((p) => (
                    <Typography key={p.uuid} variant="body2" color="text.secondary">
                      {tgl(p.tanggal)}{p.catatan ? ` · ${p.catatan}` : ''} — {p.items.map((x) => `${x.nama} ${angka(x.jumlah)}`).join(', ')} ({rupiah(p.nilai)}){p.petugas ? ` · ${p.petugas}` : ''}
                    </Typography>
                  ))}
                </Box>
              )}
              {po.pembayaran.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Riwayat pembayaran</Typography>
                  {po.pembayaran.map((p) => (
                    <Typography key={p.uuid} variant="body2" color="text.secondary">{tgl(p.tanggal)} — {rupiah(p.jumlah)} ({p.metode}){p.catatan ? ` · ${p.catatan}` : ''}{p.petugas ? ` · ${p.petugas}` : ''}</Typography>
                  ))}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {po?.status === 'draft' && (
            <>
              <Button color="error" onClick={() => aksi(() => axios.delete(`${base()}/pesanan-beli/${uuid}`, cfg), 'Draf dihapus').then(onTutup)}>Hapus draf</Button>
              <Button onClick={() => onUbah(po)}>Ubah</Button>
              <Button variant="contained" onClick={() => aksi(() => axios.post(`${base()}/pesanan-beli/${uuid}/pesan`, {}, cfg), 'Pesanan dikirim ke supplier')}>Pesan</Button>
            </>
          )}
          {po && ['dipesan', 'sebagian'].includes(po.status) && <Button variant="contained" onClick={bukaTerima}>Terima barang</Button>}
          {po?.status === 'dipesan' && po.totalDiterima === 0 && po.totalBayar === 0 && (
            <Button color="error" onClick={() => aksi(() => axios.post(`${base()}/pesanan-beli/${uuid}/batal`, {}, cfg), 'Pesanan dibatalkan')}>Batalkan</Button>
          )}
          {po?.status === 'sebagian' && <Button color="warning" onClick={() => window.confirm('Tutup PO? Sisa barang dianggap tidak jadi dikirim.') && aksi(() => axios.post(`${base()}/pesanan-beli/${uuid}/tutup`, {}, cfg), 'Pesanan ditutup')}>Tutup PO</Button>}
          {po && !['draft', 'dibatalkan'].includes(po.status) && <Button onClick={bukaBayar}>Catat pembayaran</Button>}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onTutup}>Tutup</Button>
        </DialogActions>
      </Dialog>

      <Dlg buka={sub === 'terima'} judul="Terima barang" onTutup={() => setSub(null)} onSimpan={kirim} proses={proses} galat={galat} simpanLabel="Terima" aksiTambahan={perluCatat ? <Button onClick={catatBahan}>Catat bahan di cabang ini</Button> : null}>
        <Typography variant="body2" color="text.secondary">Isi jumlah yang benar-benar datang. Kosongkan bila belum datang; sisanya tetap menunggu.</Typography>
        <TextField size="small" type="date" label="Tanggal terima" InputLabelProps={{ shrink: true }} value={f.tanggal || ''} onChange={(e) => setF({ ...f, tanggal: e.target.value })} />
        <TextField size="small" label="No. surat jalan / catatan" value={f.catatan || ''} onChange={(e) => setF({ ...f, catatan: e.target.value })} />
        {po?.items.filter((i) => i.sisa > 0).map((i) => (
          <TextField key={i.uuid} size="small" type="number" label={`${i.nama} (sisa ${angka(i.sisa)} ${i.satuan})`} value={f.jumlah?.[i.uuid] ?? ''} onChange={(e) => setF({ ...f, jumlah: { ...f.jumlah, [i.uuid]: e.target.value } })} />
        ))}
      </Dlg>

      <Dlg buka={sub === 'bayar'} judul="Catat pembayaran ke supplier" onTutup={() => setSub(null)} onSimpan={kirim} proses={proses} galat={galat} simpanLabel="Catat">
        <Typography variant="body2" color="text.secondary">Sisa utang sekarang <b>{rupiah(po?.sisaTagihan)}</b>.</Typography>
        <TextField size="small" type="number" label="Jumlah dibayar (Rp)" value={f.jumlah || ''} onChange={(e) => setF({ ...f, jumlah: e.target.value })} autoFocus />
        <TextField size="small" select label="Metode" value={f.metode || 'tunai'} onChange={(e) => setF({ ...f, metode: e.target.value })}>
          <MenuItem value="tunai">Tunai</MenuItem><MenuItem value="transfer">Transfer</MenuItem><MenuItem value="lainnya">Lainnya</MenuItem>
        </TextField>
        <TextField size="small" type="date" label="Tanggal bayar" InputLabelProps={{ shrink: true }} value={f.tanggal || ''} onChange={(e) => setF({ ...f, tanggal: e.target.value })} />
        <TextField size="small" label="Catatan" value={f.catatan || ''} onChange={(e) => setF({ ...f, catatan: e.target.value })} />
      </Dlg>
    </>
  );
};

// ---------------- Tab-tab ----------------
const TabPesanan = ({ cabang, suppliers, bahan, produk, kabari, filterSupplier, setFilterSupplier }) => {
  const [status, setStatus] = useState('');
  const [cari, setCari] = useState('');
  const qs = new URLSearchParams({ ...(cabang && { cabang }), ...(status && { status }), ...(filterSupplier && { supplier: filterSupplier }), ...(cari && { q: cari }) }).toString();
  const { data, mutate } = useSWR(`${base()}/pesanan-beli?${qs}`, ambil, { revalidateOnFocus: false });
  const daftar = Array.isArray(data?.data) ? data.data : [];
  const [form, setForm] = useState(null); // null | { awal }
  const [detail, setDetail] = useState(null);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField size="small" select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">Semua</MenuItem>
          {Object.entries(STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Supplier" value={filterSupplier} onChange={(e) => setFilterSupplier(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">Semua</MenuItem>
          {suppliers.map((s) => <MenuItem key={s.uuid} value={s.uuid}>{s.nama}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Cari nomor PO" value={cari} onChange={(e) => setCari(e.target.value)} />
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={() => setForm({ awal: null })}>+ Buat PO</Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead><TableRow><TableCell>Nomor</TableCell><TableCell>Tanggal</TableCell><TableCell>Supplier</TableCell><TableCell>Status</TableCell><TableCell align="right">Total pesanan</TableCell><TableCell align="right">Diterima</TableCell><TableCell align="right">Sisa utang</TableCell><TableCell>Pembayaran</TableCell></TableRow></TableHead>
          <TableBody>
            {daftar.length === 0 && <TableRow><TableCell colSpan={8} align="center"><Typography color="text.secondary" sx={{ py: 3 }}>Belum ada pesanan pembelian.</Typography></TableCell></TableRow>}
            {daftar.map((p) => (
              <TableRow key={p.uuid} hover sx={{ cursor: 'pointer' }} onClick={() => setDetail(p.uuid)}>
                <TableCell sx={{ fontWeight: 700 }}>{p.nomor}</TableCell>
                <TableCell>{tgl(p.tanggalPesan)}</TableCell>
                <TableCell>{p.supplier}</TableCell>
                <TableCell><Chip size="small" color={STATUS[p.status].color} label={STATUS[p.status].label} /></TableCell>
                <TableCell align="right">{rupiah(p.totalPesan)}</TableCell>
                <TableCell align="right">{rupiah(p.totalDiterima)}</TableCell>
                <TableCell align="right" sx={{ color: p.sisaTagihan > 0 ? 'error.main' : undefined, fontWeight: 700 }}>{rupiah(p.sisaTagihan)}</TableCell>
                <TableCell><Chip size="small" variant="outlined" color={BAYAR[p.statusBayar].color} label={BAYAR[p.statusBayar].label} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <FormPo
        buka={!!form} awal={form?.awal} cabang={cabang} suppliers={suppliers} bahan={bahan} produk={produk}
        onTutup={() => setForm(null)} onSelesai={(m) => { setForm(null); kabari(m); mutate(); }}
      />
      <DetailPo uuid={detail} cabang={cabang} onTutup={() => setDetail(null)} onUbah={(po) => { setDetail(null); setForm({ awal: po }); }} kabari={kabari} muat={mutate} />
    </Box>
  );
};

const TabSupplier = ({ cabang, suppliers, muat, kabari }) => {
  const [dlg, setDlg] = useState(null); // { awal? }
  const [f, setF] = useState({});
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const buka = (awal = null) => { setGalat(''); setF(awal ? { nama: awal.nama, hp: awal.hp, alamat: awal.alamat, catatan: awal.catatan, aktif: awal.aktif } : { nama: '', hp: '', alamat: '', catatan: '' }); setDlg({ awal }); };
  const simpan = async () => {
    setProses(true); setGalat('');
    try {
      if (dlg.awal) await axios.put(`${base()}/supplier/${dlg.awal.uuid}`, f, cfg);
      else await axios.post(`${base()}/supplier`, { ...f, cabang: cabang || undefined }, cfg);
      kabari('Supplier disimpan'); setDlg(null); await muat();
    } catch (e) { setGalat(pesanGalat(e)); }
    setProses(false);
  };
  const hapus = async (s) => {
    if (!window.confirm(`Hapus supplier "${s.nama}"? (Bila sudah punya pesanan, hanya dinonaktifkan.)`)) return;
    try { const r = await axios.delete(`${base()}/supplier/${s.uuid}`, cfg); kabari(r.data?.message || 'Supplier dihapus'); await muat(); } catch (e) { kabari(pesanGalat(e), 'error'); }
  };
  return (
    <Box>
      <Stack direction="row" sx={{ mb: 2 }}><Box sx={{ flex: 1 }} /><Button variant="contained" onClick={() => buka()}>+ Tambah Supplier</Button></Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead><TableRow><TableCell>Supplier</TableCell><TableCell>Kontak</TableCell><TableCell>Alamat</TableCell><TableCell align="right">Pesanan</TableCell><TableCell>Status</TableCell><TableCell align="right">Aksi</TableCell></TableRow></TableHead>
          <TableBody>
            {suppliers.length === 0 && <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary" sx={{ py: 3 }}>Belum ada supplier.</Typography></TableCell></TableRow>}
            {suppliers.map((s) => (
              <TableRow key={s.uuid} hover>
                <TableCell sx={{ fontWeight: 600 }}>{s.nama}</TableCell>
                <TableCell>{s.hp || '-'}</TableCell>
                <TableCell>{s.alamat || '-'}</TableCell>
                <TableCell align="right">{s.jumlahPesanan}</TableCell>
                <TableCell>{s.aktif ? <Chip size="small" color="success" label="Aktif" /> : <Chip size="small" label="Nonaktif" />}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => buka(s)}>Ubah</Button><Button size="small" color="error" onClick={() => hapus(s)}>Hapus</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dlg buka={!!dlg} judul={dlg?.awal ? `Ubah ${dlg.awal.nama}` : 'Tambah supplier'} onTutup={() => setDlg(null)} onSimpan={simpan} proses={proses} galat={galat}>
        <TextField label="Nama supplier" value={f.nama || ''} onChange={(e) => setF({ ...f, nama: e.target.value })} autoFocus />
        <TextField label="No. HP / WhatsApp" value={f.hp || ''} onChange={(e) => setF({ ...f, hp: e.target.value })} />
        <TextField label="Alamat" value={f.alamat || ''} onChange={(e) => setF({ ...f, alamat: e.target.value })} />
        <TextField label="Catatan (mis. tempo 7 hari)" value={f.catatan || ''} onChange={(e) => setF({ ...f, catatan: e.target.value })} />
        {dlg?.awal && <TextField select label="Status" value={f.aktif ? 'ya' : 'tidak'} onChange={(e) => setF({ ...f, aktif: e.target.value === 'ya' })}><MenuItem value="ya">Aktif</MenuItem><MenuItem value="tidak">Nonaktif</MenuItem></TextField>}
      </Dlg>
    </Box>
  );
};

const TabUtang = ({ cabang, lihatPesanan }) => {
  const { data } = useSWR(`${base()}/utang-supplier${cabang ? `?cabang=${cabang}` : ''}`, ambil, { revalidateOnFocus: false });
  const daftar = Array.isArray(data?.data) ? data.data : [];
  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">Total utang ke supplier (barang yang sudah diterima dikurangi pembayaran)</Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: (data?.totalSisa || 0) > 0 ? 'error.main' : undefined }}>{rupiah(data?.totalSisa || 0)}</Typography>
      </Paper>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead><TableRow><TableCell>Supplier</TableCell><TableCell align="right">Tagihan</TableCell><TableCell align="right">Dibayar</TableCell><TableCell align="right">Sisa utang</TableCell><TableCell align="right">PO belum lunas</TableCell><TableCell>Jatuh tempo terdekat</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {daftar.length === 0 && <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary" sx={{ py: 3 }}>Tidak ada utang ke supplier.</Typography></TableCell></TableRow>}
            {daftar.map((x) => (
              <TableRow key={x.supplieruuid} hover>
                <TableCell sx={{ fontWeight: 600 }}>{x.supplier}{x.hp ? <Typography variant="caption" color="text.secondary" component="div">{x.hp}</Typography> : null}</TableCell>
                <TableCell align="right">{rupiah(x.tagihan)}</TableCell>
                <TableCell align="right">{rupiah(x.dibayar)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, color: 'error.main' }}>{rupiah(x.sisa)}</TableCell>
                <TableCell align="right">{x.pesananBelumLunas}</TableCell>
                <TableCell>{x.jatuhTempoTerdekat ? tgl(x.jatuhTempoTerdekat) : '-'} {x.lewatTempo && <Chip size="small" color="error" label="Lewat tempo" />}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => lihatPesanan(x.supplieruuid)}>Lihat PO</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const Pembelian = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const [tab, setTab] = useState(0);
  const [cabang, setCabang] = useState('');
  const [notif, setNotif] = useState(null);
  const [filterSupplier, setFilterSupplier] = useState('');
  const { data: cabangData } = useSWR(superadmin ? `${base()}/cabangbyrole?bentuk=objek` : null, ambil);
  const daftarCabang = Array.isArray(cabangData?.data) ? cabangData.data : [];
  const adaCabang = !superadmin || !!cabang;
  const qs = superadmin && cabang ? `cabang=${cabang}` : '';
  const { data: supData, mutate: muatSup } = useSWR(adaCabang ? `${base()}/supplier?${qs}` : null, ambil, { revalidateOnFocus: false });
  const { data: bahanData } = useSWR(adaCabang ? `${base()}/bahan?${qs}${qs ? '&' : ''}semua=1` : null, ambil, { revalidateOnFocus: false });
  const { data: produkData } = useSWR(adaCabang ? `${base()}/stok-opname/produk?${qs}` : null, ambil, { revalidateOnFocus: false });
  const suppliers = useMemo(() => (Array.isArray(supData?.data) ? supData.data : []), [supData]);
  const bahan = useMemo(() => (Array.isArray(bahanData?.data) ? bahanData.data : []), [bahanData]);
  const produk = useMemo(() => (Array.isArray(produkData?.data) ? produkData.data : []), [produkData]);
  const kabari = (text, severity = 'success') => setNotif({ severity, text });
  const cab = superadmin ? cabang : '';

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Pembelian (PO)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pesan barang ke supplier, terima bertahap (stok bahan/produk bertambah saat barang tiba), dan catat pembayaran serta utang.
      </Typography>
      {superadmin && (
        <TextField size="small" select label="Cabang" value={cabang} onChange={(e) => setCabang(e.target.value)} sx={{ minWidth: 220, mb: 2 }}>
          {daftarCabang.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
        </TextField>
      )}
      {!adaCabang ? <Alert severity="info">Pilih cabang terlebih dahulu.</Alert> : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Pesanan" />
            <Tab label="Supplier" />
            <Tab label="Utang supplier" />
          </Tabs>
          {tab === 0 && <TabPesanan cabang={cab} suppliers={suppliers} bahan={bahan} produk={produk} kabari={kabari} filterSupplier={filterSupplier} setFilterSupplier={setFilterSupplier} />}
          {tab === 1 && <TabSupplier cabang={cab} suppliers={suppliers} muat={muatSup} kabari={kabari} />}
          {tab === 2 && <TabUtang cabang={cab} lihatPesanan={(id) => { setFilterSupplier(id); setTab(0); }} />}
        </>
      )}
      <Snackbar open={Boolean(notif)} autoHideDuration={5000} onClose={() => setNotif(null)}>
        {notif ? <Alert severity={notif.severity} onClose={() => setNotif(null)}>{notif.text}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default Pembelian;
